import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Two-language support for the admin panel (فارسی / English).
 * Texts are written in Persian in the components and wrapped in t("...");
 * the English version is looked up in the table below (missing entries fall
 * back to Persian). Switching language also switches the page direction by
 * clicking the template's own RTL / LTR option in the switcher, so the Xintra
 * scripts swap the bootstrap stylesheet exactly like they normally do.
 */
export type AdminLang = "fa" | "en";

const LANG_KEY = "admin-lang";

const EN: Record<string, string> = {
  // ---------- header ----------
  "جستجو": "Search",
  "انگلیسی": "English",
  "اسپانیایی": "Spanish",
  "فرانسوی": "French",
  "عربي": "Arabic",
  "آلمانی": "German",
  "چینی": "Chinese",
  "ایتالیایی": "Italian",
  "روسی": "Russian",
  "اقلام سبد خرید": "Cart Items",
  "جمع :": "Sub Total :",
  "هندزفری": "Headphones",
  "توضیح محصول": "Product description",
  "(تعداد : 1)": "(Qty : 1)",
  "(تعداد : 2)": "(Qty : 2)",
  "جمع کل :": "Total :",
  "کیف": "Bag",
  "ساعت": "Watch",
  "لباس": "Dress",
  "ساعت هوشمند": "Smart Watch",
  "تسویه حساب": "Checkout",
  "سبد خرید خالی است": "Your cart is empty",
  "محصول اضافه کنید": "Add some products",
  "مشاهده فروشگاه": "Continue shopping",
  "هشدارها": "Notifications",
  "5 خوانده نشده": "5 Unread",
  "عنوان پیام": "Message title",
  "توضیحات": "Description",
  "الان": "Now",
  "2 ساعت پیش": "2 hours ago",
  "1 روز پیش": "1 day ago",
  "5 ساعت پیش": "5 hours ago",
  "مشاهده همه": "View All",
  "هشداری وجود ندارد": "No new notifications",
  "مجتبی": "Mojtaba",
  "طراح سایت": "Web Designer",
  "حساب کاربری": "Profile",
  "ایمیل": "Email",
  "مدیریت فایل": "File Manager",
  "تنظیمات": "Settings",
  "پشتیبانی": "Support",
  "خروج": "Log Out",

  // ---------- footer ----------
  "طراحی با": "Designed with",
  "توسط": "by",

  // ---------- switcher ----------
  "تنظیمات قالب": "Theme Styles",
  "رنگ قالب": "Theme Colors",
  "حالت رنگ قالب:": "Theme Color Mode:",
  "روشن": "Light",
  "تیره": "Dark",
  "جهت:": "Directions:",
  "چپ به راست": "LTR",
  "راست به چپ": "RTL",
  "قالب ناوبری:": "Navigation Styles:",
  "عمودی": "Vertical",
  "افقی": "Horizontal",
  "سبک های منوی عمودی و افقی:": "Vertical & Horizontal Menu Styles:",
  "منو کلیک کنید": "Menu Click",
  "منو شناور": "Menu Hover",
  "نماد کلیک کنید": "Icon Click",
  "نماد شناور": "Icon Hover",
  "سبک های چیدمان منوی جانبی:": "Sidemenu Layout Styles:",
  "پیش فرض": "Default Menu",
  "منو بسته": "Closed Menu",
  "آیکن متنی": "Icon Text",
  "آیکن": "Icon Overlay",
  "جدا شده": "Detached",
  "منو جفتی": "Double Menu",
  "سبک های صفحه:": "Page Styles:",
  "منظم": "Regular",
  "کلاسیک": "Classic",
  "مدرن": "Modern",
  "سبک های عرض طرح:": "Layout Width Styles:",
  "تمام صفحه": "Full Width",
  "جعبه ای": "Boxed",
  "موقعیت های منو:": "Menu Positions:",
  "ثابت": "Fixed",
  "قابل پیمایش": "Scrollable",
  "موقعیت های سرصفحه:": "Header Positions:",
  "لودر:": "Loader:",
  "فعال": "Enable",
  "غیرفعال": "Disable",
  "رنگ های منو:": "Menu Colors:",
  "توجه: اگر می خواهید رنگ را تغییر دهید، منو به صورت پویا تغییر می کند از زیر انتخابگر رنگ اصلی تم":
    "Note: If you want to change color Menu dynamically change from below Theme Primary color picker",
  "رنگ‌های سرصفحه:": "Header Colors:",
  "توجه: اگر می‌خواهید رنگ هدر را به صورت پویا تغییر دهید، از انتخابگر رنگ اصلی قالب در زیر تغییر دهید.":
    "Note: If you want to change color Header dynamically change from below Theme Primary color picker",
  "رنگ اصلی:": "Theme Primary:",
  "قالب پس زمینه:": "Theme Background:",
  "منو با تصویر پس زمینه:": "Menu With Background Image:",
  "خرید": "Buy Now",
  "نمونه کار": "Our Portfolio",
  "بازنشانی": "Reset",

  // ---------- sidebar (resume menu) ----------
  "اصلی": "Main",
  "داشبورد": "Dashboard",
  "محتوای رزومه": "Resume Content",
  "درباره من": "About Me",
  "مهارت‌ها": "Skills",
  "لیست مهارت‌ها": "Skill List",
  "افزودن مهارت": "Add Skill",
  "نمونه‌کارها": "Portfolio",
  "لیست نمونه‌کارها": "Project List",
  "افزودن نمونه‌کار": "Add Project",
  "سوابق کاری": "Work Experience",
  "لیست سوابق کاری": "Experience List",
  "افزودن سابقه کاری": "Add Experience",
  "سوابق تحصیلی": "Education",
  "لیست سوابق تحصیلی": "Education List",
  "افزودن سابقه تحصیلی": "Add Education",
  "ارتباطات": "Communication",
  "پیام‌های تماس": "Contact Messages",
  "سایت": "Website",
  "مشاهده سایت": "View Website",

  // ---------- pages ----------
  "پنل مدیریت": "Admin Panel",
  "فارسی": "فارسی",
  "افزودن": "Add",
  "ویرایش": "Edit",
  "حذف": "Delete",
  "عملیات": "Actions",
  "مورد": "items",
  "لیست": "List of",
  "تلاش دوباره": "Try again",
  "دریافت اطلاعات از سرور انجام نشد.": "Could not load data from the server.",
  "این مورد حذف شود؟": "Delete this item?",
  "حذف انجام نشد.": "Delete failed.",
  "در حال ذخیره...": "Saving...",
  "ذخیره تغییرات": "Save Changes",
  "انصراف": "Cancel",
  "بازگشت به لیست": "Back to list",
  "این مورد پیدا نشد.": "This item was not found.",
  "ارتباط با سرور انجام نشد. دوباره تلاش کنید.": "Could not reach the server. Please try again.",
  "این فیلد را به درستی پر کنید.": "Please fill in this field correctly.",
  "عددی در بازه مجاز وارد کنید.": "Enter a number in the allowed range.",
  "دسترسی سریع": "Quick Access",
  "سال تجربه کاری": "years of experience",
  "اطلاعات «درباره من» از سرور دریافت نشد.": "Could not load the \"About me\" information.",
  "صفحه پیدا نشد": "Page not found",
  "این صفحه در پنل مدیریت وجود ندارد": "This page does not exist in the admin panel",
  "بازگشت به داشبورد": "Back to dashboard",

  // resources
  "مهارت": "Skill",
  "نمونه‌کار": "Project",
  "سابقه کاری": "Experience",
  "سابقه تحصیلی": "Education",
  "پیام": "Message",
  "نام مهارت": "Skill name",
  "درصد تسلط": "Proficiency (%)",
  "آدرس فایل رزومه (اختیاری)": "Resume file address (optional)",
  "نام پروژه": "Project name",
  "آدرس پروژه": "Project URL",
  "نام فایل تصویر": "Image file name",
  "نام فایلی که در پوشه images سرور قرار دارد، مثلا project-1.jpg":
    "File name inside the server's images folder, e.g. project-1.jpg",
  "تصویر": "Image",
  "آدرس": "URL",
  "عنوان شغلی": "Job title",
  "سال / مدت فعالیت": "Year / duration",
  "سال / مدت": "Year / duration",
  "رشته / مدرک": "Field / degree",
  "سال / مدت تحصیل": "Year / duration",
  "نام": "Name",
  "تلفن": "Phone",
  "متن پیام": "Message",
  "هنوز مهارتی ثبت نشده است.": "No skills added yet.",
  "هنوز نمونه‌کاری ثبت نشده است.": "No projects added yet.",
  "هنوز سابقه کاری ثبت نشده است.": "No work experience added yet.",
  "هنوز سابقه تحصیلی ثبت نشده است.": "No education added yet.",
  "پیامی از فرم تماس سایت دریافت نشده است.": "No messages received from the contact form yet.",

  "دسته‌بندی": "Category",
  "بک‌اند": "Backend",
  "فرانت‌اند": "Frontend",
  "ابزارها": "Tools",
  "صنعتی": "Industrial",
  "وب": "Web",
  "متن آیکون": "Icon text",
  "حداکثر ۵ کاراکتر، مثلا C# یا SQL": "Up to 5 characters, e.g. C# or SQL",
  "رنگ کارت": "Card colour",
  "کد رنگ، مثلا #8b5cf6": "Colour code, e.g. #8b5cf6",
  "نوع پروژه": "Project type",
  "برچسب بالای عنوان، مثلا مانیتورینگ صنعتی": "Label above the title, e.g. Industrial monitoring",
  "پروژه ویژه": "Featured project",
  "تکنولوژی‌ها": "Technologies",
  "با کاما جدا کنید، مثلا .NET 8, OPC UA, SignalR": "Comma separated, e.g. .NET 8, OPC UA, SignalR",
  "با کاما جدا کنید، مثلا .NET 8, OPC UA": "Comma separated, e.g. .NET 8, OPC UA",
  "لینک دمو": "Demo link",
  "لینک کد (گیت‌هاب)": "Source code link (GitHub)",
  "تاریخ شروع": "Start date",
  "تاریخ تحویل": "Delivery date",
  "درصد پیشرفت": "Progress (%)",
  "کمتر از ۱۰۰ یعنی پروژه در حال انجام است": "Below 100 means the project is in progress",
  "تصویر پروژه": "Project image",
  "شرکت / کارفرما": "Company / client",
  "همان‌طور که در سایت نمایش داده می‌شود، مثلا ۱۴۰۲ — اکنون": "Shown as is on the site, e.g. 2023 — now",
  "شغل فعلی": "Current job",
  "ترتیب نمایش": "Display order",
  "عدد کمتر بالاتر نمایش داده می‌شود": "Lower numbers are shown first",
  "دانشگاه / مؤسسه": "University / institute",
  "مثلا دانشگاه شاهرود · معدل ۱۸٫۲۰": "e.g. Shahrood University · GPA 18.20",
  "مثلا ۱۳۹۵ — ۱۳۹۹ یا گواهینامه": "e.g. 2016 — 2020 or Certificate",
  "موضوع": "Subject",
  "مشاهده فایل": "View file",
  "در حال آپلود...": "Uploading...",
  "آپلود انجام نشد.": "Upload failed.",
  "JPG، PNG، WEBP یا GIF تا ۲ مگابایت": "JPG, PNG, WEBP or GIF up to 2 MB",
  "PDF تا ۵ مگابایت": "PDF up to 5 MB",

  // about me editor
  "زیر نام در لوگو، مثلا Software Developer": "Shown under the name in the logo, e.g. Software Developer",
  "پروژه موفق": "Completed projects",
  "مشتری راضی": "Happy clients",
  "لینک گیت‌هاب": "GitHub link",
  "آیدی تلگرام": "Telegram ID",
  "بدون @، مثلا alir431": "Without @, e.g. alir431",
  "عکس پروفایل": "Profile photo",
  "فایل رزومه (PDF)": "Resume file (PDF)",
  "دکمه «دانلود رزومه» سایت همین فایل را دانلود می‌کند.": "The site's \"Download resume\" button downloads this file.",
  "هر خط یک پاراگراف جدا در سایت می‌شود.": "Each line becomes a separate paragraph on the site.",
  "اطلاعات شخصی": "Personal Information",
  "نام و نام خانوادگی": "Full name",
  "سال‌های تجربه کاری": "Years of experience",
  "تلفن / تلگرام": "Phone / Telegram",
  "تاریخ تولد": "Birth date",
  "محل سکونت": "Location",
  "نام فایل عکس پروفایل": "Profile photo file name",
  "نام فایلی که در پوشه images سرور قرار دارد، مثلا avatar.jpg":
    "File name inside the server's images folder, e.g. avatar.jpg",
  "متن درباره من": "About me text",
  "تغییرات با موفقیت ذخیره شد.": "Changes saved successfully.",
  "ذخیره اطلاعات انجام نشد. دوباره تلاش کنید.": "Saving failed. Please try again.",
  "اطلاعات فعلی از سرور دریافت نشد؛ فرم خالی نمایش داده شده است.":
    "Current information could not be loaded; the form is empty.",
  "این اطلاعات در بخش‌های «خانه»، «درباره من» و «تماس» سایت نمایش داده می‌شود.":
    "This information is shown in the Home, About and Contact sections of the website.",

  "حداکثر زمان پاسخ": "Maximum response time",
  "آخرین باکس آمار صفحه اول، مثلا ۲۴ ساعت. خالی باشد نمایش داده نمی‌شود.":
    "Last stats box on the home page, e.g. 24 hours. Hidden when empty.",

  // notifications / messages
  "مشاهده همه پیام‌ها": "View all messages",
  "پیام خوانده‌نشده‌ای وجود ندارد": "No unread messages",
  "در حال بارگذاری...": "Loading...",
  "علامت خوانده‌شده": "Mark as read",
  "مشاهده": "View",
  "بستن": "Close",
  "جدید": "New",
  "تاریخ": "Date",
  "تاریخ ارسال": "Sent",
  "پاسخ با ایمیل": "Reply by email",

  // users
  "مدیریت": "Management",
  "کاربران": "Users",
  "کاربر": "User",
  "لیست کاربران": "Users list",
  "افزودن کاربر": "Add user",
  "نام کاربری": "Username",
  "رمز عبور": "Password",
  "شما": "You",
  "مدیر": "Admin",
  "حداقل ۸ کاراکتر": "At least 8 characters",
  "برای تغییر ندادن رمز، خالی بگذارید. با تغییر رمز، آن کاربر از همه دستگاه‌ها خارج می‌شود.":
    "Leave empty to keep the password. Changing it signs that user out on every device.",
  "هنوز کاربری ثبت نشده است.": "No users yet.",
  "این نام کاربری یا ایمیل قبلا ثبت شده است.": "This username or email is already taken.",
  "اطلاعات وارد شده معتبر نیست.": "The entered data is not valid.",
};

interface LangContextValue {
  lang: AdminLang;
  t: (fa: string) => string;
  setLang: (lang: AdminLang) => void;
}

const LangContext = createContext<LangContextValue>({
  lang: "fa",
  t: (fa) => fa,
  setLang: () => {},
});

function readLang(): AdminLang {
  try {
    return localStorage.getItem(LANG_KEY) === "en" ? "en" : "fa";
  } catch {
    return "fa";
  }
}

export function AdminLangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<AdminLang>(readLang);

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const setLang = useCallback((next: AdminLang) => {
    try {
      localStorage.setItem(LANG_KEY, next);
    } catch {
      /* not remembered */
    }
    setLangState(next);
    // same as choosing LTR / RTL in the template switcher
    document.getElementById(next === "en" ? "switcher-ltr" : "switcher-rtl")?.click();
  }, []);

  const value = useMemo<LangContextValue>(
    () => ({ lang, setLang, t: (fa: string) => (lang === "en" ? EN[fa] ?? fa : fa) }),
    [lang, setLang]
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export const useAdminLang = () => useContext(LangContext);
